import { type IconName } from '@/components/Icon'
import { isPlainObject } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { merge } from 'rxjs'
import { filter, switchMap, tap } from 'rxjs/operators'
import { toStream } from '@/utils/mobx-util'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import { type Storage } from '@/infra/storage'
import { createObjectStorage, type ObjectStorage } from '@/infra/object-storage'

/**
 * Constructor argument for filters available
 */
export type FiltersDefinition<TFilterMapping> = {
  [TKey in keyof TFilterMapping]: Omit<
    FilterRegistration<TKey, TFilterMapping[TKey]>,
    'key' | 'conflictsWith'
  > & {
    /**
     * Keys of filters this one cannot be applied with; declaring one side is enough.
     */
    readonly conflictsWith?: readonly Extract<keyof TFilterMapping, string>[]
  }
}

/**
 * A registered definition for a filter and how to create one.
 */
export interface FilterRegistration<TKey, TValue> {
  readonly key: TKey
  readonly title: string
  readonly icon?: IconName
  readonly persist?: boolean
  readonly implicit?: boolean
  readonly isVisible?: () => boolean
  readonly conflictsWith?: readonly string[]
  readonly factory: (key: TKey, title: string) => FilterViewModel<TKey, TValue>
}

/**
 * A registered definition including if filter has already been added
 */
export interface FilterOption<TKey, TValue> {
  readonly key: TKey
  readonly title: string
  readonly icon?: IconName
  readonly implicit?: boolean
  readonly current: FilterViewModel<TKey, TValue> | null
  readonly isVisible: () => boolean
  /**
   * An applied filter this one conflicts with, if any.
   */
  readonly conflict: FilterViewModel<string, unknown> | null
}

/**
 * Item source to use. The signal aborts when the filter deactivates.
 */
export type FilterItemSource<V> = (
  signal?: AbortSignal,
) => Promise<FilterItem<V>[]>

/**
 * Item source factories.
 */
export const FilterItemSource = {
  /**
   * Static item source.
   *
   * @param items
   */
  static<V>(items: FilterItem<V>[]): FilterItemSource<V> {
    return () => Promise.resolve(items)
  },
}

/**
 * Item from the filter source.
 */
export interface FilterItem<V> {
  value: V
  title: string
  description?: string
}

/**
 * The resulting filters where an entry can be null or undefined.
 */
export type FiltersResult<TFilters> = {
  [P in keyof TFilters]?: TFilters[P] | null
}

/**
 * Filters in the form {@link FilterStorage} keeps them, by key.
 */
export type SerializedFilters = Record<string, unknown>

/**
 * A filter's title and value, for showing it without applying it.
 */
export interface FilterDescription {
  key: string
  title: string
  valueText: string
}

/**
 * View model options for creating a FiltersViewModel
 */
export interface FiltersViewModelOptions<TFilters extends object> {
  definitions: FiltersDefinition<TFilters>
  filterStorage?: FilterStorage
}

/**
 * A fork's filters, taken from the view model it was forked from.
 */
interface ForkedFiltersOptions {
  registrations: FilterRegistration<string, unknown>[]
}

/**
 * View model to manage available and applied filters.
 */
export class FiltersViewModel<
  TFilters extends object,
> implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _filterStorage?: FilterStorage
  private _hiddenSavedFilters: Record<string, unknown> = {}

  /**
   * Explicitly enabled filters.
   */
  @observable accessor explicitFilterVms = observable<
    FilterViewModel<string, unknown>
  >([])

  /**
   * Implicitly enabled filters.
   */
  @observable accessor implicitFilterVms = observable<
    FilterViewModel<string, unknown>
  >([])

  /**
   * All filters available to us.
   */
  @observable accessor availableFilterRegistrations: FilterRegistration<
    string,
    unknown
  >[] = []

  /**
   * Pipeline that clears explicit filters when they stop editing in
   * an invalid state.
   */
  private readonly _clearInvalidOnStopEditing$ = toStream(() =>
    this.explicitFilterVms.slice(),
  ).pipe(
    switchMap((filterVms) =>
      merge(
        ...filterVms.map((vm) =>
          toStream(() => vm.editing).pipe(
            filter((editing) => !editing && !vm.valid),
            tap(() => this.clearFilter(vm.key as keyof TFilters)),
          ),
        ),
      ),
    ),
  )

  /**
   * Stream for the currently enabled filters. Emits current value on subscribe.
   */
  readonly appliedFilters$ = toStream(() => this.appliedFilters, true)

  constructor(
    options: FiltersViewModelOptions<TFilters> | ForkedFiltersOptions,
  ) {
    if ('registrations' in options) {
      this.availableFilterRegistrations = options.registrations
    } else {
      this._filterStorage = options.filterStorage
      for (const key in options.definitions) {
        const def = options.definitions[key]
        this.availableFilterRegistrations.push({
          key,
          title: def.title,
          factory: def.factory as unknown as FilterRegistration<
            string,
            unknown
          >['factory'],
          icon: def.icon,
          implicit: def.implicit,
          persist: def.persist,
          isVisible: def.isVisible,
          conflictsWith: def.conflictsWith,
        })
      }
    }
    this._addImplicitFilters()

    this._lifecycle.manage(
      toStream(() => this._serializeFilters()).pipe(
        tap((serialized) => this._filterStorage?.save(serialized)),
      ),
    )
    this._lifecycle.manage(this._clearInvalidOnStopEditing$)
  }

  /**
   * Available explicit filter options and if they have been added
   */
  @computed
  get availableFilters(): FilterOption<string, unknown>[] {
    return this.availableFilterRegistrations
      .filter((a) => !a.implicit)
      .map((f) => ({
        key: f.key,
        title: f.title,
        icon: f.icon,
        implicit: f.implicit,
        current: this.getFilter(f.key as keyof TFilters),
        isVisible: f.isVisible ?? (() => true),
        conflict: this._findAppliedConflict(f.key),
      }))
  }

  /**
   * The filters being applied
   */
  @computed
  get appliedFilters(): FiltersResult<TFilters> {
    const result: Record<string, unknown> = {}
    for (const filter of this.explicitFilterVms) {
      result[filter.key] = filter.value
    }
    for (const filter of this.implicitFilterVms) {
      result[filter.key] = filter.value
    }
    return result as FiltersResult<TFilters>
  }

  /**
   * Count of explicit filters with a value
   */
  @computed
  get appliedFilterCount() {
    return this.explicitFilterVms.length
  }

  /**
   * Adds a filter.
   */
  @action.bound
  addFilter<TKey extends keyof TFilters>(key: TKey) {
    const [filter, filterDefinition] = this._createFilter(key)

    if (this.explicitFilterVms.some((f) => f.key === filter.key)) {
      throw new Error('Cannot apply same filter more than once')
    }
    if (this.implicitFilterVms.some((f) => f.key === filter.key)) {
      throw new Error('Cannot apply same filter more than once')
    }

    // If our filter is implicit, don't start editing.
    if (filterDefinition.implicit) {
      this.implicitFilterVms.push(filter)
      return
    }

    filter.startEditing()
    this.explicitFilterVms.push(filter)

    if (filter.refresh) {
      // The filter's view renders the failure.
      void filter.refresh().catch(() => undefined)
    }
  }

  /**
   * Removes a filter.
   */
  @action.bound
  removeFilter<TKey extends keyof TFilters>(key: TKey) {
    const idx = this.availableFilterRegistrations.findIndex(
      (f) => f.key === key,
    )

    if (idx !== -1) {
      this.availableFilterRegistrations.splice(idx, 1)
    }
  }

  /**
   * Sets editing on an appliedFilter
   */
  editFilter<TKey extends keyof TFilters>(key: TKey) {
    const filter = this.explicitFilterVms.find((f) => f.key === key)
    filter?.startEditing()
  }

  /**
   * Getter for an appliedFilter
   */
  getFilter<TKey extends keyof TFilters>(key: TKey) {
    return (
      [...this.explicitFilterVms, ...this.implicitFilterVms]?.find(
        (f) => f.key === key,
      ) ?? null
    )
  }

  /**
   * Makes a filter implicit.
   */
  @action.bound
  makeFilterImplicit<TKey extends keyof TFilters>(key: TKey) {
    const registration = this.availableFilterRegistrations.find(
      (afr) => afr.key === key,
    )

    if (!registration) {
      return
    }

    this.removeFilter(key)
    this.availableFilterRegistrations.push({ ...registration, implicit: true })
    this._loadFilters()
  }

  /**
   * Clears explicit filters
   */
  @action.bound
  clearExplicitFilters() {
    this.explicitFilterVms = observable([])
  }

  /**
   * Clears the given filter
   */
  @action.bound
  clearFilter<TKey extends keyof TFilters>(key: TKey) {
    const explFilter = this.explicitFilterVms.find((f) => f.key === key)
    if (explFilter) {
      explFilter.clearValue()
      this.explicitFilterVms.remove(explFilter)
      return
    }

    const implFilter = this.implicitFilterVms.find((f) => f.key === key)
    if (implFilter) {
      implFilter.clearValue()
      implFilter.stopEditing()
    }
  }

  /**
   * Loads initial filters and stores changes to filters as user edits them
   */
  @action.bound
  activate() {
    // Loaded before the save stream subscribes, so loading doesn't save.
    this._loadFilters()
    this._lifecycle.activate()
  }

  /**
   * Stops storing filter changes.
   */
  @action.bound
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Replaces the applied filters with stored ones.
   */
  @action.bound
  restore(serialized: SerializedFilters) {
    this._applySerialized(serialized)
  }

  /**
   * The explicitly applied filters in their stored form. Filters that aren't
   * stored, such as date ranges, are left out. Stored filters hidden here
   * are kept as they were, so they come back once shown.
   */
  serializeExplicit(): SerializedFilters {
    const result: SerializedFilters = Object.fromEntries(
      Object.entries(this._hiddenSavedFilters).filter(
        ([key]) =>
          !this.availableFilterRegistrations.find((r) => r.key === key)
            ?.implicit,
      ),
    )
    for (const filter of this.explicitFilterVms) {
      const registration = this.availableFilterRegistrations.find(
        (r) => r.key === filter.key,
      )
      const serialized = registration?.persist ? filter.serialize() : null
      if (serialized !== null && serialized !== DO_NOT_SERIALIZE) {
        result[filter.key] = serialized
      }
    }
    return result
  }

  /**
   * The values stored filters would apply, without applying them.
   */
  valuesOf(serialized: SerializedFilters): FiltersResult<TFilters> {
    const result: Record<string, unknown> = {}
    for (const filter of this._deserializeDetached(serialized)) {
      result[filter.key] = filter.value
    }
    return result as FiltersResult<TFilters>
  }

  /**
   * Stored filters as titles and values, without applying them.
   */
  describe(serialized: SerializedFilters): FilterDescription[] {
    return this._deserializeDetached(serialized).map((filter) => ({
      key: filter.key,
      title: filter.title,
      valueText: filter.valueText,
    }))
  }

  /**
   * A copy that starts from stored filters and offers only the explicit ones
   * that can be stored, for editing a set of filters away from the applied
   * ones. It doesn't persist anything.
   */
  fork(serialized: SerializedFilters): FiltersViewModel<TFilters> {
    const forked = new FiltersViewModel<TFilters>({
      registrations: this.availableFilterRegistrations.filter(
        (r) => !r.implicit && r.persist,
      ),
    })
    forked.restore(serialized)
    return forked
  }

  /**
   * Serializes filters so they may be stored
   */
  private _serializeFilters(): Record<string, unknown> {
    const obj: Record<string, unknown> = { ...this._hiddenSavedFilters }

    for (const filter of [
      ...this.explicitFilterVms,
      ...this.implicitFilterVms,
    ]) {
      const filterDefinition = this.availableFilterRegistrations.find(
        (d) => d.key === filter.key,
      )

      if (!filterDefinition?.persist) {
        continue
      }

      const serialized = filter.serialize()

      if (serialized !== DO_NOT_SERIALIZE) {
        obj[filter.key] = serialized
      }
    }

    return obj
  }

  /**
   * Loads stored filters, deserializes them, and applies them.
   */
  @action.bound
  private _loadFilters() {
    this._applySerialized(this._filterStorage?.load())
  }

  @action.bound
  private _applySerialized(loaded: SerializedFilters | undefined) {
    this._hiddenSavedFilters = {}
    if (!loaded) {
      return
    }

    this._clearAllFilters()
    for (const filterRegistration of this._storedIn(loaded)) {
      // A filter saved where it was visible (like the redesign's "Unread")
      // can't be seen or removed where it is hidden, so it must not apply.
      // It is still saved back, so it returns once the filter is visible.
      if (isHidden(filterRegistration)) {
        this._hiddenSavedFilters[filterRegistration.key] =
          loaded[filterRegistration.key]
        continue
      }

      const serialized = loaded[filterRegistration.key]

      const filter =
        this.implicitFilterVms.find((r) => r.key === filterRegistration.key) ??
        filterRegistration.factory(
          filterRegistration.key,
          filterRegistration.title,
        )

      filter.deserialize(serialized)

      if (filter.valid && filterRegistration.implicit) {
        this.implicitFilterVms.remove(filter)
        this.implicitFilterVms.push(filter)
      } else if (filter.valid) {
        this.explicitFilterVms.push(filter)
      }
    }
  }

  /**
   * Creates filters from their stored form without applying them, leaving
   * out ones that aren't stored, are hidden or are no longer valid.
   */
  private _deserializeDetached(serialized: SerializedFilters) {
    return this._storedIn(serialized).flatMap((registration) => {
      if (isHidden(registration)) {
        return []
      }
      const filter = registration.factory(registration.key, registration.title)
      filter.deserialize(serialized[registration.key])
      return filter.valid ? [filter] : []
    })
  }

  /**
   * The registrations of the stored filters that can be stored.
   */
  private _storedIn(serialized: SerializedFilters) {
    return this.availableFilterRegistrations.filter(
      (r) => r.persist && r.key in serialized,
    )
  }

  /**
   * Finds an applied filter that conflicts with the given one.
   */
  private _findAppliedConflict(key: string) {
    const applied = [...this.explicitFilterVms, ...this.implicitFilterVms]
    return (
      applied.find((filter) => this._areInConflict(key, filter.key)) ?? null
    )
  }

  /**
   * Whether either filter declares a conflict with the other.
   */
  private _areInConflict(left: string, right: string) {
    const conflictsOf = (key: string) =>
      this.availableFilterRegistrations.find((r) => r.key === key)
        ?.conflictsWith ?? []
    return (
      conflictsOf(left).includes(right) || conflictsOf(right).includes(left)
    )
  }

  /**
   * Creates a new filter view model from the filter registration.
   *
   * @param key
   * @returns
   */
  private _createFilter<TKey extends keyof TFilters>(key: TKey) {
    const filterDefinition = this.availableFilterRegistrations.find(
      (d) => d.key === key,
    )
    if (!filterDefinition) {
      throw new Error(`Missing a filter definition for key ${key.toString()}`)
    }

    return [
      filterDefinition.factory(key as string, filterDefinition.title),
      filterDefinition,
    ] as const
  }

  /**
   * Adds all implicitly defined filters.
   */
  private _addImplicitFilters() {
    for (const registration of this.availableFilterRegistrations) {
      if (registration.implicit) {
        this.addFilter(registration.key as keyof TFilters)
      }
    }
  }

  /**
   * Clears all filters.
   */
  private _clearAllFilters() {
    this.explicitFilterVms = observable([])
    this.implicitFilterVms = observable([])
    this._addImplicitFilters()
  }
}

function isHidden(registration: FilterRegistration<string, unknown>) {
  return !!registration.isVisible && !registration.isVisible()
}

/**
 * Contract that a filter view model must satisfy.
 */
export interface FilterViewModel<TKey, TValue> {
  /**
   * The field being filtered on.
   */
  readonly key: TKey
  /**
   * The title of the filter.
   */
  readonly title: string
  /**
   * Value of the filter.
   */
  readonly value: TValue | null
  /**
   * Whether the filter is being edited or not
   */
  readonly editing: boolean
  /**
   * Whether the filter is valid or not
   */
  readonly valid: boolean
  /**
   * The value for display, without the title.
   */
  readonly valueText: string
  /**
   * Method to prepare filter value for display purposes
   */
  toString: () => string
  /**
   * Method to clear the filter value
   */
  clearValue: () => void
  /**
   * Starts editing the filter with the specified key if it has been applied.
   */
  startEditing: () => void
  /**
   * Stops editing the filter with the specified key if it has been applied.
   */
  stopEditing: () => void
  /**
   * Serialize the filter into a form that can be stored in localStorage.
   */
  serialize: () => unknown
  /**
   * Deserialize the filter into a form that can be set as a value.
   */
  deserialize: (value: unknown) => void

  /**
   * Initialize the filter
   * @returns
   */
  refresh?: () => Promise<void>
}

/**
 * Class to handle storing and retrieving filters from local storage.
 */
export class FilterStorage {
  private readonly _key
  private readonly _storage: ObjectStorage

  constructor(storage: Storage, namespace: string) {
    this._key = `WorklayerFilters.${namespace}`

    this._storage = createObjectStorage(storage)
  }

  /**
   * Loads the stored filters, or an empty object.
   */
  load = (): Record<string, unknown> => {
    const res = this._storage.getItem(this._key)

    if (isPlainObject(res)) {
      return res as Record<string, unknown>
    }

    return {}
  }

  /**
   * Stores the filters.
   *
   * @param filters
   */
  save = (filters: Record<string, unknown>) => {
    this._storage.setItem(this._key, filters)
  }
}

/**
 * Delegate to create a filter storage for a namespace.
 */
export type FilterStorageFactory = (namespace: string) => FilterStorage

/**
 * Returned by FilterViewModel to tell us when we don't want to serialize that value
 */
export const DO_NOT_SERIALIZE = Symbol('DO_NOT_SERIALIZE')
